import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { getEffectiveAuthenticatedAccountId } from "@/lib/accountContext";
import { apiFetch, buildParams } from "@/lib/api";
import { navigationQueryKey } from "@/lib/authQueryCache";
import { fallbackNavigationSlugsForRole, permissionsFromNavigation } from "@/lib/navigation";
import { useUserStore } from "@/store/userStore";
import type { NavigationOutput } from "@/types";

export function useNavigation() {
  const authenticatedAccountId = useUserStore((s) => s.authenticatedAccountId);
  const role = useUserStore((s) => s.user.role);
  const availableRoles = useUserStore((s) => s.availableRoles);
  const permissions = useUserStore((s) => s.permissions);
  const hasHydrated = useUserStore((s) => s.hasHydrated);
  const permissionsInitialized = useUserStore((s) => s.permissionsInitialized);
  const initializePermissions = useUserStore((s) => s.initializePermissions);
  const clearPermissions = useUserStore((s) => s.clearPermissions);
  const resolvedAccountId = getEffectiveAuthenticatedAccountId(authenticatedAccountId) ?? "";
  const hasVerifiedRole = !!role && availableRoles.includes(role);
  const query = buildParams({
    account_id: resolvedAccountId,
    role: role || "",
  });

  const result = useQuery({
    queryKey: [...navigationQueryKey, query] as const,
    queryFn: () => apiFetch<NavigationOutput>(`/v1/navigation${query}`),
    enabled: !!resolvedAccountId && hasVerifiedRole && hasHydrated,
  });

  const prevQueryRef = useRef(query);
  useEffect(() => {
    if (prevQueryRef.current !== query) {
      clearPermissions();
      prevQueryRef.current = query;
    }
  }, [clearPermissions, query]);

  useEffect(() => {
    if (!result.isError) return;

    if (permissionsInitialized) {
      initializePermissions(permissions);
      return;
    }

    initializePermissions(permissionsFromNavigation(fallbackNavigationSlugsForRole(role)));
  }, [initializePermissions, permissions, permissionsInitialized, result.isError, role]);

  useEffect(() => {
    if (result.data) {
      initializePermissions(permissionsFromNavigation(result.data.items));
    }
  }, [initializePermissions, result.data, role]);

  return result;
}
